import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaClock, FaCalendarAlt, FaChartLine } from 'react-icons/fa';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import { logout } from '../../../../utils/api/auth';
import { fetchOrganiserTimeSalesAnalytics } from '../../../../utils/api/organiserApi';
import TimeSalesCards from '../../../../components/TimeSalesCards.jsx';
import AnalyticsCharts from '../../../../components/AnalyticsCharts.jsx';
import '../css/TimeSalesAnalysis.css';

function TimeSalesAnalysis() {
  const nav = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState('all');
  const [analyticsData, setAnalyticsData] = useState({
    timeBasedSales: [],
    events: [],
    hourlyTrends: [],
    weeklyPatterns: []
  });

  useEffect(() => {
    fetchTimeSalesData();
  }, [selectedEvent]);

  const fetchTimeSalesData = async () => {
    try {
      setLoading(true);
      setError(null);
      const eventFilter = selectedEvent === 'all' ? null : selectedEvent;
      console.log('[TimeSalesAnalysis] ⏱️ Fetching — API: fetchOrganiserTimeSalesAnalytics()', { eventFilter });
      const response = await fetchOrganiserTimeSalesAnalytics(eventFilter);
      console.log('[TimeSalesAnalysis] ✅ Data loaded:', {
        columns: 'timeBasedSales, events[{event_id,event_name}], hourlyTrends[{hour,tickets,revenue}], weeklyPatterns[{day,morning,afternoon,evening,night}], totalTickets, peakSalesTime',
        formula: 'timePeriods: morning=06-12h, afternoon=12-17h, evening=17-21h, night=21-06h',
        data: response,
      });
      setAnalyticsData({
        timeBasedSales: response.timeBasedSales || [],
        events: response.events || [],
        hourlyTrends: response.hourlyTrends || [],
        weeklyPatterns: response.weeklyPatterns || [],
        totalTickets: response.totalTickets || 0,
        peakSalesTime: response.peakSalesTime || '',
      });
    } catch (error) {
      console.error('[TimeSalesAnalysis] ❌ Error fetching time sales data:', error);
      setError('Unable to load time sales data. Please try again later.');
      setAnalyticsData({
        timeBasedSales: [],
        events: [],
        hourlyTrends: [],
        weeklyPatterns: []
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout(nav);
  };

  const handleEventChange = (eventId) => {
    setSelectedEvent(eventId);
  };

  // Prepare data for components
  const charts = [
    {
      type: 'line',
      data: analyticsData.hourlyTrends,
      title: 'Hourly Sales Trends (24 Hours)',
      xAxisKey: 'hour',
      lines: [
        { dataKey: 'tickets', color: '#EFB034FF', name: 'Tickets Sold' },
        { dataKey: 'revenue', color: '#c3e1f8', name: 'Revenue' }
      ],
      height: 350
    },
    {
      type: 'bar',
      data: analyticsData.weeklyPatterns,
      title: 'Weekly Sales Patterns by Time Period',
      xAxisKey: 'day',
      bars: [
        { dataKey: 'morning', color: '#c3e1f8', name: 'Morning', order: 0 },
        { dataKey: 'afternoon', color: '#b8f5cd', name: 'Afternoon', order: 1 },
        { dataKey: 'evening', color: '#f8cedb', name: 'Evening', order: 2 },
        { dataKey: 'night', color: '#bcbff5', name: 'Night', order: 3 }
      ],
      height: 350
    }
  ];

  if (loading) {
    return (
      <div className="time-sales-analysis">
        <GlobalHeader
          title="Time Sales Analysis"
          subtitle="Loading time sales data..."
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <main className="time-sales-analysis__main">
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="time-sales-analysis">
        <GlobalHeader
          title="Time Sales Analysis"
          subtitle="Error loading data"
          onLogout={handleLogout}
          backTo="/organiser-dash"
        />
        <main className="time-sales-analysis__main">
          <div className="error-container">
            <div className="error-message">{error}</div>
            <button onClick={fetchTimeSalesData} className="retry-button">
              Retry
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="time-sales-analysis">
      <GlobalHeader
        title="Time Sales Analysis"
        subtitle="Detailed analysis of sales patterns by time of day"
        onLogout={handleLogout}
        backTo="/organiser-dash"
      />

      <main className="time-sales-analysis__main">
        {/* Time-Based Sales Analysis */}
        <TimeSalesCards 
          timeData={analyticsData.timeBasedSales}
          events={analyticsData.events}
          onEventChange={handleEventChange}
          selectedEvent={selectedEvent}
        />

        {/* Charts Section */}
        <section className="charts-section">
          <AnalyticsCharts charts={charts} />
        </section>
      </main>
    </div>
  );
}

export default TimeSalesAnalysis;
